/**
 * No-orphan guard for display titles (Lars, PFM #83 review 2026-09-24:
 * "Don't orphan that in the heading").
 *
 * Works together with `text-wrap: balance` on `.blog-hero__title`. Balance
 * evens the lines; this closes the cases balance cannot — measured on staging:
 * "Why / Performance / …" at 770-1400px, where "Why Performance" does not fit
 * the column and balance strands "Why".
 *
 * Rule: a short word (1-3 letters, e.g. "a", "to", "the", "Why") is joined to
 * the word after it with a non-breaking space, unless it is already joined to
 * the word before it; a short LAST word is joined to the word before it.
 * Joins never chain, so no unbreakable run longer than a pair (rarely three
 * words) is created. Measured: joining every short word forward chained
 * "to How the Key Comes" into one unit and stranded the final "Up".
 *
 * Render-time only: the stored title (markdown, metadata, JSON-LD) is
 * untouched. Only ordinary spaces change, so the visible text is identical.
 */
const SHORT = /^["'‘“(]?[A-Za-z]{1,3}[.,:;!?'"’”)]*$/;

export function noOrphan(title: string): string {
  const words = title.split(' ');
  if (words.length < 2) return title;
  // joined[i] === true  →  the gap after words[i] becomes a non-breaking space
  const joined: boolean[] = new Array(words.length - 1).fill(false);
  for (let i = 0; i < words.length - 1; i++) {
    const joinedFromPrev = i > 0 && joined[i - 1];
    if (SHORT.test(words[i]) && !joinedFromPrev && words[i + 1] !== '') joined[i] = true;
  }
  const last = words.length - 1;
  if (SHORT.test(words[last]) && !joined[last - 1] && words[last - 1] !== '') joined[last - 1] = true;
  return words.reduce((out, w, i) => (i === 0 ? w : out + (joined[i - 1] ? ' ' : ' ') + w), '');
}
